import { motion } from "framer-motion";
import { Download, ArrowUpRight, MapPin, Apple } from "lucide-react";
import { MaskLines } from "@/components/Reveal";
import { PhoneMockup } from "@/components/PhoneMockup";
import { APK_URL, APP_STORE_URL } from "@/lib/brand";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative grain overflow-hidden bg-[#2B2B2B] text-white pt-32 pb-20 lg:pt-40 lg:pb-28"
    >
      <div className="absolute inset-0 techgrid-dark opacity-70" />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#2BBFB3]/10 blur-3xl" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left copy */}
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 overline text-[#2BBFB3]"
          >
            <MapPin size={13} /> Built in Australia · for instructors
          </motion.span>

          <h1 className="mt-6 font-display font-extrabold tracking-tight text-[13vw] sm:text-6xl lg:text-[5.2rem] leading-[0.92]">
            <MaskLines
              lines={["Run your driving", "school smarter."]}
              lineClass="text-white"
            />
            <span className="block overflow-hidden">
              <motion.span
                className="block text-[#2BBFB3]"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                Not harder.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-lg text-white/70 leading-relaxed"
          >
            DriveAssure turns every lesson into structured, spatial feedback — a source of truth
            with GPS pin drops, digital records and shareable progress reports. The professional
            toolkit for driving instructors who take their business seriously.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3"
          >
            <a
              href={APK_URL}
              data-testid="hero-download-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2BBFB3] px-7 py-4 font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
            >
              <Download size={18} /> Download for Android
            </a>
            <a
              href={APP_STORE_URL}
              data-testid="hero-download-ios-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2BBFB3] px-7 py-4 font-semibold text-[#0f1211] hover:bg-[#1FA097] transition-colors"
            >
              <Apple size={18} /> Download for iOS
            </a>
            <a
              href="#founder"
              data-testid="hero-founder-btn"
              className="group inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-4 font-semibold text-white hover:border-[#F5A623] hover:text-[#F5A623] transition-colors"
            >
              Join the Founder Program
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/50"
          >
            <span>GPS-anchored feedback</span>
            <span className="hidden sm:inline text-white/20">/</span>
            <span>PDF lesson records</span>
            <span className="hidden sm:inline text-white/20">/</span>
            <span className="text-[#F5A623]">Founder price locked for life</span>
          </motion.div>
        </div>

        {/* Right mockup */}
        <motion.div
          initial={{ opacity: 0, y: 40, rotate: 2 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 flex justify-center lg:justify-end"
        >
          <motion.div
            animate={{ y: [0, -14, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          >
            <PhoneMockup />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
